<template>
    <div>
        <div class="row">
            <div class="col-2">
                <div class="tw-fieldset-box-label">
                    {{locale}}
                </div>
            </div>
            <div class="col">

                <div v-if="contents.length === 0 && !overwriteMode">
                    <div class="row">
                        <div class="col">
                            <span class="text-muted">No contents</span>
                        </div>
                        <div class="col text-right">
                            <a href="#" @click="create" class="tw-content-field-inheritance-action overwrite-button">Set value</a>
                        </div>
                    </div>
                </div>

                <div v-else>
                    <div v-if="ownContent && !removeOverwriteMode">
                        <input type="hidden" :name="inputNamePrefix + '[typeKey]'" :value="contentType"/>
                        <input type="hidden" :name="inputNamePrefix + '[dataString]'" :value="contentFormDataString"/>
                        <input type="hidden" :name="inputNamePrefix + '[action]'" value="update"/>
                        <div class="row">
                            <div class="col">
                                <component :is="contentType" render-mode="edit" :content-value="ownContentObject.value" :metadata="ownContent._contentMetadata" @update-value="handleContentFormUpdate" :options="configuration.options" />
                            </div>
                            <div class="col text-right">
                                <div v-if="inheritedContent">
                                    Overwrites {{inheritsFrom === 'partnerGroup' ? 'partner group' : 'system'}}<br>
                                    <a href="#" @click="removeOverwrite" class="tw-content-field-inheritance-action remove-overwrite-button">Remove overwrite</a>
                                </div>
                                <div v-else>
                                    No inheritance<br>
                                    <a href="#" @click="removeOverwrite" class="tw-content-field-inheritance-action remove-overwrite-button">Remove value</a>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="ownContent && removeOverwriteMode">
                        <input type="hidden" :name="inputNamePrefix + '[typeKey]'" :value="contentType"/>
                        <input type="hidden" :name="inputNamePrefix + '[dataString]'" :value="contentFormDataString"/>
                        <input type="hidden" :name="inputNamePrefix + '[action]'" value="remove">
                        <div class="row">
                            <div class="col">
                                <div v-if="inheritedContent">
                                    <component :is="contentType" render-mode="view" :content-value="inheritedContentObject.value" :metadata="inheritedContent._contentMetadata" @update-value="handleContentFormUpdate" :options="configuration.options" />
                                </div>
                                <div v-else>
                                    <span class="text-muted">No data.</span>
                                </div>
                            </div>
                            <div class="col text-right">
                                Removing <span v-if="inheritedContent">overwrite</span><span v-else>value</span> on save<br>
                                <a href="#" @click="cancelRemoveOverwrite" class="tw-content-field-inheritance-action undo-remove-overwrite-button">Undo</a>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="overwriteMode">
                        <input type="hidden" :name="inputNamePrefix + '[typeKey]'" :value="contentType"/>
                        <input type="hidden" :name="inputNamePrefix + '[dataString]'" :value="contentFormDataString"/>
                        <input type="hidden" :name="inputNamePrefix + '[action]'" value="create">
                        <div class="row">
                            <div class="col">
                                <component :is="contentType" render-mode="create"  @update-value="handleContentFormUpdate" :options="configuration.options" />
                            </div>
                            <div class="col text-right">
                                <div v-if="!isFirstValue">
                                    Overwriting {{inheritsFrom === 'partnerGroup' ? 'partner group' : 'system'}} on save<br>
                                </div>
                                <div v-else>
                                    Setting value on save
                                </div>
                                <a href="#" @click="cancelOverwrite" class="tw-content-field-inheritance-action cancel-overwrite-button">Cancel</a>
                            </div>
                        </div>
                    </div>

                    <div v-else-if="inheritedContent">
                        <div class="row">
                            <div class="col">
                                <component :is="contentType" render-mode="view" :content-value="inheritedContentObject.value" @update-value="handleContentFormUpdate" :metadata="inheritedContent._contentMetadata" :options="configuration.options" />
                            </div>
                            <div class="col text-right">
                                Inherits from {{inheritsFrom === 'partnerGroup' ? 'partner group' : 'system'}}<br>
                                <a href="#" @click="overwrite" class="tw-content-field-inheritance-action overwrite-button">Overwrite value</a>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>

    </div>
</template>

<script>
  import _ from 'lodash';
  import TextContent from './ContentTypes/TextContent';
  import ImageContent from './ContentTypes/ImageContent';

  export default {
    name: "ContentFields",

    methods: {
      create () {
        this.overwriteMode = true;
        this.isFirstValue = true;
      },
      overwrite () {
        this.overwriteMode = true;
        this.isFirstValue = false;
      },
      cancelOverwrite () {
        this.overwriteMode = false;
        this.isFirstValue = false;
      },
      removeOverwrite () {
        this.removeOverwriteMode = true;
      },
      cancelRemoveOverwrite () {
        this.removeOverwriteMode = false;
      },
      handleContentFormUpdate (data) {
        this.contentFormData = data;
      }
    },

    data: () => ({
      contentFormData: null,
      overwriteMode: false,
      removeOverwriteMode: false,
      isFirstValue: false
    }),

    computed: {
      contentFormDataString () {
        return JSON.stringify({
          type: this.contentType,
          value: this.contentFormData
        })
      },

      inputNamePrefix () {
        return `content[${this.contentKey}][${this.locale}]`;
      },

      contentType () {
        return this.configuration.type;
      },
      partnerContent () {
        return _.find(this.contents, (content) => {
          return content.partner_id !== null && content.partner_group_id === null;
        });
      },
      partnerGroupContent () {
        return _.find(this.contents, (content) => {
          return content.partner_group_id !== null && content.partner_id === null;
        });
      },
      systemContent () {
        return _.find(this.contents, (content) => {
          return content.partner_id === null && content.partner_group_id === null;
        });
      },
      /**
       * The serialized content object belonging to the content record
       * @returns {any}
       */
      ownContentObject () {
        return this.ownContent ? JSON.parse(this.ownContent.content) : null;
      },
      /**
       * Same as ownContentObject but for the inherited content
       * @returns {any}
       */
      inheritedContentObject () {
        return this.inheritedContent ? JSON.parse(this.inheritedContent.content) : null;
      },
      /**
       * The content belonging to the actor (e.g. the partner, the partner group or the system)
       * @returns {null|*}
       */
      ownContent () {
        if (this.partnerId && this.partnerContent) {
          return this.partnerContent;
        }

        if (this.partnerGroupId && this.partnerGroupContent) {
          return this.partnerGroupContent;
        }

        if (!this.partnerGroupId && !this.partnerId && this.systemContent) {
          return this.systemContent;
        }

        return null;
      },
      /**
       * The source the inherited content comes from.
       * @returns {string|null}
       */
      inheritsFrom () {
        if (this.partnerId && this.partnerGroupContent) {
          return 'partnerGroup'
        }

        if (this.partnerId && !this.partnerGroupContent && this.systemContent) {
          return 'system'
        }

        if (this.partnerGroupId && !this.partnerId && this.systemContent) {
          return 'system'
        }

        return null;
      },
      /**
       * The inherited content, as opposed to ownContent which is the content
       * directly belonging to the active partner/partnergroup/system
       * @returns {null|*}
       */
      inheritedContent () {
        switch (this.inheritsFrom) {
          case 'partnerGroup':
            return this.partnerGroupContent;
          case 'system':
            return this.systemContent;
          default:
            return null;
        }
      }
    },

    props: {
      locale: {
        type: String,
        required: true
      },
      contentKey: {
        type: String
      },
      contents: {
        type: Array,
        required: true
      },
      partnerId: {
        type: String,
        default: null
      },
      partnerGroupId: {
        type: String,
        default: null
      },
      configuration: {
        type: Object,
        required: true
      }
    },

    components: {
      TextContent,
      ImageContent
    }
  };
</script>

<style scoped>

</style>